import { Box, CircularProgress } from '@mui/material';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy } from 'react';

import Layout from '../components/layout/Layout';
import DailyPage from '../pages/daily/DailyPage';
import CreateActivityPage from '../pages/activity/CreateActivityPage';
import WeekPage from '../pages/week/WeekPage';
import ViewActivityPage from '../pages/activity/ViewActivityPage';
import EditActivityPage from '../pages/activity/EditActivityPage';
import { CalendarScene } from '../contexts/CalendarContext';
import { RoutineScene } from '../contexts/RoutineContext';
import SettingsPage from '../pages/settings/SettingsPage';
import NotFoundPage from '../pages/NotFound/NotFoundPage';
import SuperviseGuard from './SuperviseGuard';
import AiRoute from './AiRoute';
import PushNotificationProvider from '../PushNotificationsProvider';
import { LocationScene } from '@/contexts/LocationContext';
import { CharacterScene } from '@/contexts/CharacterContext';
import { StarScene } from '@/contexts/StarContext';
import ChildOnboardingGate from './ChildOnboardingGate';
import ChildRouteSwitch from './ChildRouteSwitch';
import ChildHome from '../pages/child/ChildHome';
import ChildWeek from '../pages/child/ChildWeek';
import StarsPage from '../pages/child/StarsPage';
import ChildCreateWizard from '../pages/child/ChildCreateWizard';
import ChildActivityPage from '../pages/child/activity/ChildActivityPage';
import PersonaView from '@/personas/PersonaView';
import SupervisorHub from '../pages/Supervise/SupervisorHub';
import RouteErrorBoundary from './RouteErrorBoundary';

/**
 * Lazy-laddade rutter. Startbundlen låg på 934 kB gzippad, och målgruppen startar
 * ofta appen på mobildata -- då är varje kB första intrycket. Dessa vyer är antingen
 * TUNGA (statistiken drar in apexcharts, anteckningarna drar in dnd-kit) eller
 * SÄLLAN besökta (handledarens administration, onboarding, butiken).
 *
 * Dagsvyn, veckovyn, aktivitetsvyn och skapa-flödet laddas EAGER med flit: de är
 * appens vardag, och en spinner mellan "öppna appen" och "se dagen" hade varit ett
 * sämre byte än några kB.
 */
const StatisticsPage = lazy(() => import('src/pages/Statistics/StatisticsPage'));
const AIProfilePage = lazy(() => import('src/pages/ai/AIProfilePage'));
const NotesPage = lazy(() => import('../pages/notes/NotesPage'));
const SupervisorShop = lazy(() => import('../pages/Supervise/SupervisorShop'));
const SupervisorGoals = lazy(() => import('../pages/Supervise/SupervisorGoals'));
const SupervisorStarValues = lazy(() => import('../pages/Supervise/SupervisorStarValues'));
const BulkCreateActivities = lazy(() => import('../pages/Supervise/BulkCreateActivities'));
const RoutineEditor = lazy(() => import('../pages/Supervise/RoutineEditor'));
const TimerDisplaySettings = lazy(() => import('../pages/Supervise/TimerDisplaySettings'));
const SupervisorPersona = lazy(() => import('../pages/Supervise/SupervisorPersona'));
const SupervisorRoutineDisplay = lazy(() => import('../pages/Supervise/SupervisorRoutineDisplay'));
const CleanupActivities = lazy(() => import('../pages/Supervise/CleanupActivities'));
const ChildShopPage = lazy(() => import('../pages/child/ChildShopPage'));
const ChildPurchasesPage = lazy(() => import('../pages/child/ChildPurchasesPage'));
const CharacterSelectPage = lazy(() => import('../pages/onboarding/CharacterSelectPage'));
const ChildOnboarding = lazy(() => import('../pages/onboarding/ChildOnboarding'));

/**
 * Platshållare medan en lazy-laddad vy hämtas. Medvetet lugn och centrerad: en
 * hoppande layout eller en blinkande spinner är sämre för NPF-målgruppen än en
 * stillsam indikator. Höjden matchar innehållsytan så navbaren inte flyttar sig.
 */
const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress aria-label="Laddar" />
  </Box>
);

const AuthRouter = () => {
  const location = useLocation();

  return (
    <CalendarScene>
      <PushNotificationProvider />
      {/* Rutinerna läses en gång och delas av dag-, vecka- och aktivitetsvyerna:
          instanserna i kalendern bär inte stegen, och "en sak i taget" behöver dem. */}
      <RoutineScene>
        <LocationScene>
          <CharacterScene>
            <StarScene>
              <ChildOnboardingGate>
                <Layout>
                  {/* En Suspense runt hela rutt-trädet: fallbacken syns bara när en
                    lazy-laddad vy hämtas första gången, och Layout står redan kvar
                    runt den så navigeringen inte hoppar. */}
                  {/* Felgränsen INNANFÖR Layout: navbaren och handledarhuvudet står kvar
                      när en enskild vy fallerar, så det finns en väg vidare. key:en på
                      sökvägen nollställer gränsen vid navigering — annars hade felrutan
                      blivit kvar även på nästa sida. */}
                  <RouteErrorBoundary key={location.pathname}>
                    <Suspense fallback={<RouteFallback />}>
                      <Routes>
                        <Route
                          path={'/'}
                          element={
                            <SuperviseGuard>
                              <PersonaView route="/" child={<ChildHome />} adult={<DailyPage />} />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/login'}
                          element={
                            <SuperviseGuard>
                              <PersonaView route="/" child={<ChildHome />} adult={<DailyPage />} />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/create-activity/:formId'}
                          element={
                            <SuperviseGuard>
                              <ChildRouteSwitch
                                child={<ChildCreateWizard />}
                                adult={<CreateActivityPage />}
                              />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/activity/:instanceId'}
                          element={
                            <SuperviseGuard>
                              <ChildRouteSwitch
                                child={<ChildActivityPage />}
                                adult={<ViewActivityPage />}
                              />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/edit-activity/:instanceId/:editSingleActivity/:formId'}
                          element={
                            <SuperviseGuard>
                              <EditActivityPage />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/week'}
                          element={
                            <SuperviseGuard>
                              <PersonaView
                                route="/week"
                                child={<ChildWeek />}
                                adult={<WeekPage />}
                              />
                            </SuperviseGuard>
                          }
                        />
                        {/* Barn-only stjärnvy; vuxen-/handledarläge redirectas till '/' i StarsPage. */}
                        <Route
                          path={'/stars'}
                          element={
                            <SuperviseGuard>
                              <StarsPage />
                            </SuperviseGuard>
                          }
                        />
                        {/* Barn-only stjärnbutik (SHOP-03); nås från StarsPage. Samma
                      barn-only-mönster som '/stars' — ChildShopPage redirectar
                      själv till '/' i vuxen-/handledarläge. */}
                        <Route
                          path={'/shop'}
                          element={
                            <SuperviseGuard>
                              <ChildShopPage />
                            </SuperviseGuard>
                          }
                        />
                        {/* Barn-only "Mina köp" (SHOP-06): barnets sätt att VISA en vuxen att
                      något är köpt. Nås från ChildShopPage/StarsPage och direkt efter
                      köp. Samma barn-only-mönster — ChildPurchasesPage redirectar
                      själv till '/' i vuxen-/handledarläge. */}
                        <Route
                          path={'/shop/purchases'}
                          element={
                            <SuperviseGuard>
                              <ChildPurchasesPage />
                            </SuperviseGuard>
                          }
                        />
                        {/* Persona-egen framstegsvy: teen → TeenProgress, barn → StarsPage
                      (fallback), vuxen/young → redirect till '/'. */}
                        <Route
                          path={'/progress'}
                          element={
                            <SuperviseGuard>
                              <PersonaView
                                route="/progress"
                                child={<StarsPage />}
                                adult={<Navigate to="/" replace />}
                              />
                            </SuperviseGuard>
                          }
                        />
                        {/* KVAR och nåbara i handledarläge — bara dolda i barn-nav. */}
                        <Route
                          path={'/notes'}
                          element={
                            <SuperviseGuard>
                              <NotesPage />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/statistics'}
                          element={
                            <SuperviseGuard>
                              <StatisticsPage />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/ai-profile'}
                          element={
                            <SuperviseGuard>
                              <AiRoute>
                                <AIProfilePage />
                              </AiRoute>
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/onboarding'}
                          element={
                            <SuperviseGuard>
                              <ChildRouteSwitch
                                child={<ChildOnboarding />}
                                adult={<CharacterSelectPage />}
                              />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/supervisor'}
                          element={
                            <SuperviseGuard>
                              <SupervisorHub />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/supervisor/shop'}
                          element={
                            <SuperviseGuard>
                              <SupervisorShop />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/supervisor/goals'}
                          element={
                            <SuperviseGuard>
                              <SupervisorGoals />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/supervisor/star-values'}
                          element={
                            <SuperviseGuard>
                              <SupervisorStarValues />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/supervisor/routine'}
                          element={
                            <SuperviseGuard>
                              <RoutineEditor />
                            </SuperviseGuard>
                          }
                        />
                        {/* Samma formulär som skapandet, med rutinen ifylld. Handledarens
                        tryck på en rutin leder hit (ViewActivityPage skickar vidare). */}
                        <Route
                          path={'/supervisor/routine/:activityId'}
                          element={
                            <SuperviseGuard>
                              <RoutineEditor />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/supervisor/cleanup'}
                          element={
                            <SuperviseGuard>
                              <CleanupActivities />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/supervisor/persona'}
                          element={
                            <SuperviseGuard>
                              <SupervisorPersona />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/supervisor/timer-display'}
                          element={
                            <SuperviseGuard>
                              <TimerDisplaySettings />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/supervisor/routine-display'}
                          element={
                            <SuperviseGuard>
                              <SupervisorRoutineDisplay />
                            </SuperviseGuard>
                          }
                        />
                        <Route
                          path={'/supervisor/bulk-create'}
                          element={
                            <SuperviseGuard>
                              <BulkCreateActivities />
                            </SuperviseGuard>
                          }
                        />
                        <Route path={'/settings'} element={<SettingsPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </Suspense>
                  </RouteErrorBoundary>
                </Layout>
              </ChildOnboardingGate>
            </StarScene>
          </CharacterScene>
        </LocationScene>
      </RoutineScene>
    </CalendarScene>
  );
};

export default AuthRouter;
